//* 상조관리자의 정산 내용 페이지

import { FC, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  monthlyAccountedDataAtom,
  monthlyAccountedStatusAtom,
  needAccountDataAtom,
  needAccountDateTypeAtom,
  needAccountStatusAtom,
} from '@shared/api/needAccount/needAccount.atom';
import {
  useMonthlyAccountedHook,
  useNeedAccountHook,
} from '@shared/api/needAccount/needAccount.hook';
import { Loading } from '@shared/components/Loading';
import { ReloadButton } from '@shared/components/ReloadButton';
import StatPageSideBar from '@shared/components/StatPageSideBar';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import { agencyAdminAccountSelectYearAtom } from '../containers/AgencyAdminAccount/AgencyAdminAccount.atom';
import { AnnualAccountedTable } from '../containers/AgencyAdminAccount/AnnualAccountedTable';
import { NeedAccountTable } from '../containers/AgencyAdminAccount/NeedAccountTable';
import { useTitleHook } from '../hooks/useTitleHook';
import ErrorPage from './ErrorPage';

const AgencyAdminAccountPage: FC = function AgencyAdminAccountPage() {
  useTitleHook('정산내역');

  const location = useLocation();
  const showSettings = useMemo(
    () => new URLSearchParams(location.search).get('settings') === 'true',
    [location.search],
  );

  const selectYear = useRecoilValue(agencyAdminAccountSelectYearAtom);
  const selectedYearDate = dayjs(selectYear).toDate();
  const selectedYear = selectedYearDate.getFullYear();

  //* needAccount: 미정산 내역 (연도 무관)
  const [dateType, setDateType] = useRecoilState(needAccountDateTypeAtom);
  // settings 모드가 아니면 dateType을 보내지 않음 (백엔드가 bugoBrand.accountDateType을 자동 적용)
  const effectiveDateType = showSettings ? dateType : undefined;
  useNeedAccountHook(effectiveDateType);
  const needAccountStatus = useRecoilValue(needAccountStatusAtom);
  const needAccountData = useRecoilValue(needAccountDataAtom);

  //* monthlyAccounted: 정산완료 내역 (연도별 + dateType 공유)
  useMonthlyAccountedHook(selectedYear, effectiveDateType);
  const monthlyAccountedStatus = useRecoilValue(monthlyAccountedStatusAtom);
  const monthlyAccountedData = useRecoilValue(monthlyAccountedDataAtom);

  //*mobile 기준
  const isMobile = !useMediaQuery({
    query: '(min-width: 800px)',
  });

  if (isMobile) {
    return (
      <div>
        <ErrorPage
          text="정산 페이지는 모바일 환경에서 지원하지 않습니다."
          subText="PC환경에서 접속해주시거나 화면의 가로길이를 늘려주세요."
        />
      </div>
    );
  }

  // 정산예정내역: 최초 로드 전이면 로딩, 이후에는 데이터 유지
  const needAccountMonthly = needAccountData?.monthly ?? null;
  const needAccountLoading = needAccountStatus !== 'success';

  // 정산완료내역: 최초 로드 전이면 로딩, 연도 변경 시 기존 데이터 위에 오버레이
  const hasAccountedData = !!monthlyAccountedData;
  const accountedRefetching = monthlyAccountedStatus !== 'success';

  return (
    <div className="flex">
      {/* Sidebar */}
      <div className="flex flex-col">
        <StatPageSideBar />
      </div>
      <div className="mx-auto max-w-screen-2xl flex-1">
        <div className="py-8 px-10">
          <div className="flex justify-between space-x-10">
            <div>
              <p className="text-20 font-bold">연간 정산내역</p>
            </div>
            <div className="center-box h-full gap-4">
              <ReloadButton />
            </div>
          </div>
          <div className="space-y-4 py-4">
            {/* 정산예정내역: 연도와 무관, 독립 렌더 */}
            {needAccountMonthly ? (
              <div className="relative">
                {needAccountLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Loading />
                  </div>
                )}
                <NeedAccountTable
                  needAccountMonthly={needAccountMonthly}
                  dateType={effectiveDateType}
                  onDateTypeChange={setDateType}
                />
              </div>
            ) : (
              <div className="center-box py-8">
                <Loading />
              </div>
            )}

            {/* 정산완료내역: 연도 변경 시 기존 데이터 유지 + 오버레이 */}
            {hasAccountedData ? (
              <div className="relative">
                {accountedRefetching && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Loading />
                  </div>
                )}
                <AnnualAccountedTable
                  monthlyAccounted={monthlyAccountedData.monthly}
                  summary={monthlyAccountedData.summary}
                  selectedYear={selectedYear}
                />
              </div>
            ) : (
              <div className="center-box py-8">
                <Loading />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyAdminAccountPage;
